/**
 * Time ago function that returns both a long and short relative (minutes to years) date string.
 *
 * ! Note, formatting is specific to the Activity Feed.
 *
 * Examples:
 * { long: '2 minutes ago', short: '2m' }
 * { long: '2 hours ago', short: '2h' }
 * { long: '2 days ago', short: '2d' }
 * { long: '2 weeks ago', short: '2w' }
 * { long: '2 months ago', short: '<localized date string, e.g. M/D/YY>' }
 * { long: '2 years ago', short: '<localized date string, e.g. M/D/YY>' }
 */
export const timeAgo = date => {
  const now = new Date();
  const relativeFormat = new Intl.RelativeTimeFormat();
  const secondsPast = Math.floor((now.getTime() - date.getTime()) / 1000);
  const oneMinute = 60;
  if (secondsPast < oneMinute) {
    // less than 1 minute shows "1m" (instead of seconds)
    return {
      full: relativeFormat.format(-1, 'minute'),
      short: '1m'
    };
  }
  const oneHour = 3600;
  if (secondsPast < oneHour) {
    const minutes = Math.floor(secondsPast / 60);
    return {
      full: relativeFormat.format(-1 * minutes, 'minute'),
      short: `${minutes}m`
    };
  }
  const oneDay = 86400;
  if (secondsPast < oneDay) {
    const hours = Math.floor(secondsPast / 3600);
    return {
      full: relativeFormat.format(-1 * hours, 'hour'),
      short: `${hours}h`
    };
  }
  const oneWeek = 604800;
  if (secondsPast < oneWeek) {
    const days = Math.floor(secondsPast / 86400);
    return {
      full: relativeFormat.format(-1 * days, 'day'),
      short: `${days}d`
    };
  }
  const oneMonth = 2592000;
  if (secondsPast < oneMonth) {
    const weeks = Math.floor(secondsPast / 604800);
    return {
      full: relativeFormat.format(-1 * weeks, 'week'),
      short: `${weeks}w`
    };
  }

  // For month and older, show the date (e.g. D/M/YY)

  const dateFormat = Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'numeric',
    year: '2-digit'
  });
  const oneYear = 31536000;
  if (secondsPast < oneYear) {
    const months = Math.floor(secondsPast / 2592000);
    return {
      full: relativeFormat.format(-1 * months, 'month'),
      short: dateFormat.format(date)
    };
  }
  const years = Math.floor(secondsPast / 31536000);
  return {
    full: relativeFormat.format(-1 * years, 'year'),
    short: dateFormat.format(date)
  };
};